import React, { useCallback, useRef } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { TipTapEditor } from '../../../TipTap';
import { styled } from '@mui/material/styles';

const EditorWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  '& .tiptap-editor-container': {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
  },
  '& .tiptap-editor-container:focus-within': {
    borderColor: theme.palette.text.secondary,
  },
  '& .tiptap-editor-content': {
    padding: '0 !important',
    minHeight: 'auto !important',
  },
  '& .ProseMirror': {
    fontSize: '12px',
    minHeight: '60px',
    padding: '8px 10px',
    outline: 'none',
    border: 'none',
    '& p': { margin: 0 },
    '& p + p': { marginTop: '4px' },
  },
}));

export default function DisplayPreview({ field, onUpdate, palette, theme, availablePlaceholders }) {
  const editorRef = useRef(null);
  const contentFormat = field.contentFormat || 'html';
  const isMarkdown = contentFormat === 'markdown';

  const handleContentChange = useCallback(
    (val) => onUpdate({ content: val }),
    [onUpdate]
  );

  const handleFormatChange = useCallback(
    (e, newFormat) => {
      if (newFormat) {
        onUpdate({ contentFormat: newFormat });
      }
    },
    [onUpdate]
  );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '10px', color: palette.textSecondary, opacity: 0.7 }}>
          Read-only content shown to the user
        </Typography>
        <ToggleButtonGroup
          value={contentFormat}
          exclusive
          onChange={handleFormatChange}
          size="small"
          sx={{
            height: '20px',
            '& .MuiToggleButton-root': {
              fontSize: '10px',
              fontWeight: 600,
              px: '8px',
              py: 0,
              textTransform: 'none',
              borderColor: palette.border,
              color: palette.textSecondary,
              '&.Mui-selected': {
                backgroundColor: palette.textSecondary,
                color: palette.surface,
                '&:hover': { backgroundColor: palette.textSecondary },
              },
            },
          }}
        >
          <ToggleButton value="html">HTML</ToggleButton>
          <ToggleButton value="markdown">Markdown</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <EditorWrapper>
        <TipTapEditor
          ref={editorRef}
          value={field.content || ''}
          onChange={handleContentChange}
          showToolbar={true}
          stripHtml={false}
          markdown={isMarkdown}
          placeholder="Enter display content..."
          minHeight="60px"
          availablePlaceholders={availablePlaceholders}
        />
      </EditorWrapper>
    </Box>
  );
}
